import { createClient } from '@supabase/supabase-js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase/client';
import { useAuth } from './AuthProvider';



export const useTables = () => {
  const {profile} = useAuth(); 

  return useQuery({
    queryKey: ['tables'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tables')
        .select('id, section_id, shape, seats, x, y, width, height, rotation, reservation_ids, display_number')
        .order('section_id').eq("restaurantid",profile.restaurantid);
      if (error) throw error;
      const result = (data || []).map(table => ({
        ...table,
        id: Number(table.id),
        reservation_ids: table.reservation_ids.map(Number),
        display_number: table.display_number || table.id, // Fallback to id if display_number is null
      }));
      return result;
    },
    enabled: !!supabase.auth.getSession(),
  });
};

export const useTableById = (tableId) => {
  return useQuery({
    queryKey: ['table', tableId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tables')
        .select('id, section_id, shape, seats, x, y, width, height, rotation, reservation_ids, display_number')
        .eq('id', Number(tableId))
        .single();
      if (error) throw error;
      if (!data) throw new Error(`Table with ID ${tableId} not found`);
      return {
        ...data,
        id: Number(data.id),
        reservation_ids: data.reservation_ids.map(Number),
        display_number: data.display_number || data.id,
      };
    },
    enabled: !!tableId && !!supabase.auth.getSession(),
  });
};

export const useAddTable = () => {
  const queryClient = useQueryClient();
    const {profile} = useAuth(); 


  return useMutation({
    mutationFn: async (tableData) => {
      const { reservation_ids, ...rest } = tableData;
      const { data, error } = await supabase
        .from('tables')
        .insert([{ ...rest, reservation_ids: reservation_ids.map(Number), display_number: rest.display_number,restaurantid:profile.restaurantid }])
        .select('id, section_id, shape, seats, x, y, width, height, rotation, reservation_ids, display_number')
        .single();
      if (error) throw error;
      return {
        ...data,
        id: Number(data.id),
        reservation_ids: data.reservation_ids.map(Number),
        display_number: data.display_number,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });
};

export const useUpdateTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableData) => {
      const { id, x, y, rotation, reservation_ids, display_number } = tableData;

      const { data, error } = await supabase
        .from('tables')
        .update({ x, y, rotation, reservation_ids: reservation_ids.map(Number), display_number })
        .eq('id', Number(id))
        .select('id, section_id, shape, seats, x, y, width, height, rotation, reservation_ids, display_number')
        .single();
      if (error) {
        console.error("Supabase update error:", error);
        throw error;
      }
      return {
        ...data,
        id: Number(data.id),
        reservation_ids: data.reservation_ids.map(Number),
        display_number: data.display_number,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
    onError: (error) => {
      console.error("Mutation error in useUpdateTable:", error);
    },
  });
};

export const useDeleteTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }) => {
      const { error } = await supabase
        .from('tables')
        .delete()
        .eq('id', Number(id));
      if (error) throw error;
      return { id: Number(id) };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['table', result.id] });
    },
  });
};